import { useState, useMemo, Dispatch, SetStateAction } from 'react';
import { StorageHook, useSessionStorageState, useLocalStorageState } from '@clover-util/use-storage-state';

export type CollectionDispatch<T> = {
  add: (...newItems: T[]) => void;
  update: (index: number, item: T) => void;
  remove: (index: number) => void;
  clear: () => void;
};

type CollectionResult<T> = [T[], CollectionDispatch<T>] & CollectionDispatch<T> & { items: T[] };

function useCollectionDispatch<T>(setItems: Dispatch<SetStateAction<T[]>>): CollectionDispatch<T> {
  return useMemo(() => ({
    add(...newItems) { return setItems(items => !newItems.length ? items : [...items, ...newItems]); },
    update(index, item) {
      setItems(items => {
        if (index < 0) return [...items, item];

        return [...items.slice(0, index), item, ...items.slice(index + 1)];
      });
    },
    remove(index) {
      setItems(items => {
        if (index < 0 || index >= items.length) return items;

        return [...items.slice(0, index), ...items.slice(index + 1)];
      });
    },
    clear() { setItems(items => !items.length ? items : []); },
  }), [setItems]);
}

/**
 * A hook which provides add, update, remove, clear and replace methods to augment an array in state
 *
 * @example
 * const [ words, wordsCollection ] = useCollection();
 *
 * wordsCollection.add('hello'); // [ 'hello' ]
 * wordsCollection.update(0, 'hello world'); // [ 'hello world' ]
 * wordsCollection.remove(0); // []
 * wordsCollection.clear(); // []
 *
 * @param initializer initial array of items in the collection
 */
export function useCollection<T>(initializer: (T[] | (() => T[])) = []): CollectionResult<T> {
  const [items, setItems] = useState(initializer);

  const collection = useCollectionDispatch(setItems);

  return Object.assign([items, collection], { items, ...collection }) as CollectionResult<T>;
}

function createStorageCollectionHook(useHook: StorageHook) {
  /**
   * A hook which provides add, update, remove, clear and replace methods to augment an array in state and synced
   * with window storage
   *
   * @example
   * const [ words, wordsCollection ] = useStorageCollection(SessionStorage, 'words', []);
   *
   * wordsCollection.add('hello'); // [ 'hello' ]
   * wordsCollection.update(0, 'hello world'); // [ 'hello world' ]
   * wordsCollection.remove(0); // []
   * wordsCollection.clear(); // []
   *
   * @param key {string} key of value stored store din storage
   * @param initializer initial value of items in collection
   */
  return function useStorageCollection<T>(key: string | undefined, initializer: (T[] | (() => T[])) = []): CollectionResult<T> {
    const [items, setItems] = useHook(key, initializer);

    const collection = useCollectionDispatch(setItems);

    return Object.assign([items, collection], { items, ...collection }) as CollectionResult<T>;
  };
}

export const useSessionStorageCollection = createStorageCollectionHook(useSessionStorageState);
export const useLocalStorageCollection = createStorageCollectionHook(useLocalStorageState);
